import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/auth-api";

/**
 * .NET Desktop Runtime x86 na stanowiskach z pharmind XF (Kamsoft wymaga 9 i 10
 * równolegle). Wszystkie trzy endpointy tylko dla super_admina (inaczej 403).
 * Ścieżki bez `/api` — apiFetch dokleja prefix + Bearer.
 */

export interface DotnetRequirement {
	framework: string;
	arch: "x86" | "x64";
	major: number;
}

export interface DotnetInstaller {
	major: number;
	arch: string;
	version: string;
}

export interface DotnetRuntimeEntry {
	framework: string;
	arch: string;
	version: string;
}

export type DotnetInstallBlocker =
	| "server_group"
	| "agent_outdated"
	| "not_scanned"
	| "nothing_missing"
	| "install_in_progress";

export type DotnetInstallStatus = "pending" | "sent" | "succeeded" | "failed" | "expired";

export interface DotnetDeviceState {
	deviceUuid: string;
	/** Stanowisko apteczne (grupa „Apteki”, także serwer apteczny) — liczy się do banera. */
	inScope: boolean;
	status: "ok" | "missing" | "not_scanned";
	/** Brakujące majory wg reguły (np. [10] albo [9, 10]); puste dla ok/not_scanned. */
	missingMajors: number[];
	/** WSZYSTKIE znalezione runtime'y (też NETCore/AspNetCore/x64). */
	runtimes: DotnetRuntimeEntry[];
	scannedAt: string | null;
	scanPending: boolean;
	lastScanError: string | null;
	lastScanAttemptAt: string | null;
	/** Agent >= scanMinAgentVersion. */
	canScan: boolean;
	installBlocker: DotnetInstallBlocker | null;
	install: {
		status: DotnetInstallStatus;
		requestedAt: string;
		finishedAt: string | null;
		versions: string[];
		rebootRequired: boolean;
		error: string | null;
	} | null;
}

export interface DotnetRuntimeOverview {
	required: DotnetRequirement[];
	installers: DotnetInstaller[];
	installMinAgentVersion: string;
	scanMinAgentVersion: string;
	devices: DotnetDeviceState[];
}

export type DotnetScanOutcome = "sent" | "queued" | "in_progress" | "agent_outdated" | "not_found";

export type DotnetInstallOutcome =
	| "sent"
	| "error"
	| "offline"
	| "agent_outdated"
	| "server_group"
	| "not_scanned"
	| "nothing_missing"
	| "in_progress"
	| "not_found";

export interface DotnetScanResponse {
	results: { deviceUuid: string; outcome: DotnetScanOutcome }[];
}

export interface DotnetInstallResponse {
	results: { deviceUuid: string; outcome: DotnetInstallOutcome; versions: string[] }[];
}

export function getDotnetRuntimeOverview(): Promise<DotnetRuntimeOverview> {
	return apiFetch<DotnetRuntimeOverview>("/device-agent/dotnet-runtime");
}

/** Limit backendu na jedno żądanie; „Zaznacz wszystkie” na całej flocie go przekracza. */
export const DOTNET_BATCH_LIMIT = 500;

/** Wysyła po kolei paczkami ≤ DOTNET_BATCH_LIMIT i skleja wyniki. */
async function postInBatches<R>(path: string, deviceUuids: string[]): Promise<{ results: R[] }> {
	const results: R[] = [];
	for (let i = 0; i < deviceUuids.length; i += DOTNET_BATCH_LIMIT) {
		const res = await apiFetch<{ results: R[] }>(path, {
			method: "POST",
			body: JSON.stringify({ deviceUuids: deviceUuids.slice(i, i + DOTNET_BATCH_LIMIT) }),
		});
		results.push(...res.results);
	}
	return { results };
}

export function scanDotnetRuntime(deviceUuids: string[]): Promise<DotnetScanResponse> {
	return postInBatches("/device-agent/dotnet-runtime/scan", deviceUuids);
}

export function installDotnetRuntime(deviceUuids: string[]): Promise<DotnetInstallResponse> {
	return postInBatches("/device-agent/dotnet-runtime/install", deviceUuids);
}

export const DOTNET_RUNTIME_QUERY_KEY = ["device-agent", "dotnet-runtime"] as const;

/** Szybki poll tylko, gdy czekamy na wynik skanu albo instalacji. */
export const DOTNET_RUNTIME_ACTIVE_POLL_MS = 15_000;

export function dotnetRuntimeRefetchInterval(
	data: DotnetRuntimeOverview | undefined,
): number | false {
	const active = data?.devices.some(
		(d) => d.scanPending || d.install?.status === "pending" || d.install?.status === "sent",
	);
	return active ? DOTNET_RUNTIME_ACTIVE_POLL_MS : false;
}

/** `enabled` = super_admin — innym backend odpowiada 403, więc nie pytamy wcale. */
export function useDotnetRuntimeQuery(enabled: boolean) {
	return useQuery<DotnetRuntimeOverview>({
		queryKey: DOTNET_RUNTIME_QUERY_KEY,
		queryFn: getDotnetRuntimeOverview,
		enabled,
		// Scheduler odświeża dane skanu co ~60 s.
		staleTime: 60_000,
		refetchInterval: (query) => dotnetRuntimeRefetchInterval(query.state.data),
	});
}

export function useDotnetScanMutation() {
	const qc = useQueryClient();
	return useMutation<DotnetScanResponse, Error, string[]>({
		mutationFn: scanDotnetRuntime,
		onSettled: () => qc.invalidateQueries({ queryKey: DOTNET_RUNTIME_QUERY_KEY }),
	});
}

export function useDotnetInstallMutation() {
	const qc = useQueryClient();
	return useMutation<DotnetInstallResponse, Error, string[]>({
		mutationFn: installDotnetRuntime,
		onSettled: () => qc.invalidateQueries({ queryKey: DOTNET_RUNTIME_QUERY_KEY }),
	});
}
